import type { EdgeReleaseStatus as Release } from '../lib/api';

const LABELS: Record<Release['state'], string> = {
  unknown: 'Unknown', paused: 'Paused', offline: 'Offline', applying: 'Applying',
  stalled: 'Stalled', healthy: 'Healthy', behind: 'Behind', rollback: 'Rolled back',
  quarantined: 'Quarantined', repair_required: 'Needs repair',
};

const COLORS: Record<Release['state'], string> = {
  unknown: 'text-slate-600 border-slate-200 bg-slate-50',
  paused: 'text-amber-700 border-amber-200 bg-amber-50',
  offline: 'text-red-700 border-red-200 bg-red-50',
  applying: 'text-blue-700 border-blue-200 bg-blue-50',
  stalled: 'text-red-700 border-red-200 bg-red-50',
  healthy: 'text-emerald-700 border-emerald-200 bg-emerald-50',
  behind: 'text-amber-700 border-amber-200 bg-amber-50',
  rollback: 'text-amber-700 border-amber-200 bg-amber-50',
  quarantined: 'text-red-700 border-red-200 bg-red-50',
  repair_required: 'text-red-700 border-red-200 bg-red-50',
};

export function EdgeReleaseBadge({ release }: { release?: Release }) {
  if (!release) return <span className="text-xs text-slate-500">Status unavailable</span>;
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${COLORS[release.state]}`}
      title={release.failureCode ?? undefined}>
      {LABELS[release.state]}
    </span>
  );
}

export function EdgeReleaseDetails({ release }: { release?: Release }) {
  if (!release) return <p className="text-sm text-slate-600">Release status is unavailable.</p>;
  const { desired, installed } = release;
  const short = (value: string | null | undefined) => value ? value.slice(0, 12) : '—';
  return (
    <div className="space-y-3 text-xs text-slate-700">
      <div className="flex items-center gap-2"><EdgeReleaseBadge release={release} />
        {release.failureCode && <span className="font-mono text-slate-600">{release.failureCode}</span>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2">
        <div><span className="text-slate-500">Assigned runtime</span><div className="font-mono">{short(desired?.runtimeSha)}{desired && ` · revision ${desired.revision}`}</div></div>
        <div><span className="text-slate-500">Reported runtime</span><div className="font-mono">{short(installed.runtimeSha)}</div></div>
        <div><span className="text-slate-500">Root and user corroborated</span><div className="font-mono">{short(installed.corroboratedRuntimeSha)}</div></div>
        <div><span className="text-slate-500">Controller target / installed</span><div className="font-mono">{short(desired?.controlPlaneSha256)} / {short(installed.controlPlaneSha256)}</div></div>
        <div><span className="text-slate-500">Mac app target / verified</span><div className="font-mono">{desired?.macAppVersion ?? '—'} / {installed.macAppVersion ?? '—'}</div></div>
        <div><span className="text-slate-500">Cohort</span><div className="font-mono">{desired?.cohort ?? '—'}{desired?.paused ? ' · paused' : ''}</div></div>
        <div><span className="text-slate-500">Last successful release check</span><div>{release.lastUpdateCheckAt ? new Date(release.lastUpdateCheckAt).toLocaleString() : '—'}</div></div>
        <div><span className="text-slate-500">Transition age</span><div>{release.transitionAgeSec === null ? '—' : `${Math.floor(release.transitionAgeSec / 60)} min`}</div></div>
        <div><span className="text-slate-500">Failed / recovery runtime</span><div className="font-mono">{short(release.failedSha)} / {short(desired?.recoverySha)}</div></div>
      </div>
    </div>
  );
}
